import { useState, useEffect, useCallback, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Space, Button, Typography, Alert, Input, Select, Row, Col, Modal } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AddDriverModal from "../../../components/Ouber/modals/admin/AdminAddDriverModal";
import AdminViewDriverModal from "../../../components/Ouber/modals/admin/AdminViewDriverModal";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";

/**
 * The purpose of this page is to manage the roster of drivers, 
 * track their contact details, and view their assigned trips.
 */
const ManageDriverPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [filterDriver, setFilterDriver] = useState("");
  const [filterContact, setFilterContact] = useState("");
  const [filterType, setFilterType] = useState(null);
  const [filterAssignment, setFilterAssignment] = useState(null);


  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  // Retrieves the driver list from the backend with filters for name, contact, type, and assignment status
  const fetchDrivers = useCallback(async () => {
    try {
      const params = {
        search_name: filterDriver || undefined,
        contact_info: filterContact || undefined,
        driver_type: filterType || undefined,
        has_assignment: filterAssignment !== null ? filterAssignment : undefined,
      };
      const response = await ouberService.getDrivers(params);
      
      const driversList = Array.isArray(response) ? response : (response?.data || []);

      const drivers = driversList.map(d => ({ 
        ...d, 
        key: d.driver_id.toString(), 
        id: d.driver_id.toString() 
      }));
      
      // Formats raw driver data into a consistent UI-friendly object structure
      setData(drivers);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load drivers.");
    }
  }, [filterDriver, filterContact, filterType, filterAssignment, ouberService]);

  const fetchDriversRef = useRef(fetchDrivers);
  useEffect(() => {
    fetchDriversRef.current = fetchDrivers;
  }, [fetchDrivers]);

  // Implements a debounced fetch to handle live search filtering for the driver list
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDriversRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterDriver, filterContact, filterType, filterAssignment]);

  const filteredData = data;

  const handleEditDriver = (driver) => {
    setSelectedDriver(driver);
    setIsModalOpen(true);
  };

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    setIsViewModalOpen(true);
  };

  const handleCreateDriver = () => {
    setSelectedDriver(null);
    setIsModalOpen(true);
  };

  const handleDeleteDriver = (driver) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this driver?',
      content: `This will permanently remove ${driver.driver_name} from the registry.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ouberService.deleteDriver(driver.id);
          showSuccessToast("Success", "Driver deleted successfully.");
          fetchDrivers();
        } catch (e) {
          showErrorToast("Error", "Failed to delete driver. They might be assigned to a trip.");
        }
      }
    });
  };

  const handleSaveDriver = async (values) => {
    try {
      if (selectedDriver) {
        await ouberService.updateDriver(selectedDriver.id, values);
        showSuccessToast("Success", "Driver updated successfully.");
      } else {
        await ouberService.createDriver(values);
        showSuccessToast("Success", "Driver created successfully.");
      }
      setIsModalOpen(false);
      fetchDrivers();
    } catch (e) {
      showErrorToast("Error", "Failed to save driver.");
    }
  };

  const columns = [
    { title: "Driver ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Driver Name",
      dataIndex: "driver_name",
      key: "driver_name",
      align: "center",
    },
    {
      title: "Contact Information",
      dataIndex: "contact_number",
      key: "contact_number",
      align: "center",
    },
    { 
      title: "Driver Type", 
      dataIndex: "driver_type", 
      key: "driver_type", 
      align: "center",
    },
    {
      title: "Next Assignment",
      key: "next_assignment",
      align: "center",
      render: (_, record) => {
        const next = record.next_assignment;
        if (!next) return <span style={{ color: "#bfbfbf", fontSize: "12px" }}>None Scheduled</span>;
        
        return (
          <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
            <div style={{ fontWeight: "bold", color: "#862633" }}>
              {next.trip_date ? next.trip_date.split('T')[0] : 'N/A'}
            </div>
            <div style={{ color: "#595959" }}>{next.dropoff_location || next.pickup_venue}</div>
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
            onClick={() => handleViewDriver(record)}
          />
          <EditOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
            onClick={() => handleEditDriver(record)}
          />
          {isSuperAdmin && (
            <DeleteOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#f5222d" }}
              onClick={() => handleDeleteDriver(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Drivers
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          className="ouber-btn-forest"
          onClick={handleCreateDriver}
        >
          Create Driver
        </Button>
      </Space>

      <Alert
        className="ouber-alert"
        message={<span className="ouber-alert-title">Driver Registry</span>}
        description={
          <span className="ouber-alert-desc">
            View, edit, and manage system-wide drivers, their contact information, types, and next trip assignments.
          </span>
        }
        type="info"
        showIcon
      />

      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Driver Name" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Contact Info" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterContact}
              onChange={(e) => setFilterContact(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: "100%" }}
              allowClear
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={[
                { value: "Employee", label: "Employee" },
                { value: "Outsourced", label: "Outsourced" },
                { value: "Carpool", label: "Carpool" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Has Assignment?"
              style={{ width: "100%" }}
              allowClear
              value={filterAssignment}
              onChange={(val) => setFilterAssignment(val)}
              options={[
                { value: true, label: "Yes, Has Assignment" },
                { value: false, label: "No, Free" },
              ]}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button 
          icon={<SyncOutlined />} 
          onClick={fetchDrivers} 
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Sync Data
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={columns}
          loading={loading}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={columns}
          onRowClick={() => {}}
          loading={loading}
          maxActiveRow={filteredData.length}
        />
      )}

      <AddDriverModal 
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSaveDriver}
        initialValues={selectedDriver}
      />

      <AdminViewDriverModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        driver={selectedDriver}
      />
    </div>
  );
};

export default ManageDriverPage;
